import { useState, useEffect } from 'react';
import { Eye } from 'lucide-react';
import CivicModal from './CivicModal';
import {
  getExplorerLookDeeperScenario,
  type LookDeeperScenario,
} from '../lib/explorerLookDeeperService';

interface ExplorerLookDeeperModalProps {
  songId: string;
  songTitle: string;
  onClose: () => void;
}

type Step = 'scenario' | 'outcome';
type Choice = 'A' | 'B' | null;

export default function ExplorerLookDeeperModal({
  songId,
  songTitle,
  onClose,
}: ExplorerLookDeeperModalProps) {
  const [scenario, setScenario] = useState<LookDeeperScenario | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState<Step>('scenario');
  const [selectedChoice, setSelectedChoice] = useState<Choice>(null);

  useEffect(() => {
    loadScenario();
  }, [songId]);

  async function loadScenario() {
    setLoading(true);
    const { data, error } = await getExplorerLookDeeperScenario(songId);
    if (error) {
      console.error('Error loading Look Deeper scenario:', error);
    }
    if (data) {
      setScenario(data.scenario);
    }
    setLoading(false);
  }

  function handleChoiceSelect(choice: 'A' | 'B') {
    setSelectedChoice(choice);
    setCurrentStep('outcome');
  }

  function handleBackToScenario() {
    setCurrentStep('scenario');
  }

  if (loading) {
    return (
      <CivicModal
        isOpen={true}
        onClose={onClose}
        title="Look Deeper"
        icon={Eye}
        maxWidth="md"
      >
        <div className="flex items-center justify-center py-12">
          <div className="text-[#A0A0A0]">Loading scenario...</div>
        </div>
      </CivicModal>
    );
  }

  if (!scenario) {
    return (
      <CivicModal
        isOpen={true}
        onClose={onClose}
        title="Look Deeper"
        icon={Eye}
        maxWidth="md"
      >
        <div className="flex items-center justify-center py-12">
          <div className="text-[#A0A0A0]">
            No scenario available for this song yet.
          </div>
        </div>
      </CivicModal>
    );
  }

  const subtitle = currentStep === 'scenario'
    ? 'A quick scenario. Two choices. No right answer.'
    : '';

  return (
    <CivicModal
      isOpen={true}
      onClose={onClose}
      title="Look Deeper"
      subtitle={subtitle}
      icon={Eye}
      maxWidth="md"
    >
      {/* Step 1: Scenario */}
      {currentStep === 'scenario' && (
        <div className="bg-[#1A1A1A] rounded-xl border border-[#282828] p-5 sm:p-6">
          {/* Setup Text */}
          <div className="text-[#E0E0E0] text-[14px] leading-relaxed whitespace-pre-line mb-6 text-left">
            {scenario.setup_text}
          </div>

          {/* Choices */}
          <div className="space-y-3">
            <button
              onClick={() => handleChoiceSelect('A')}
              className="w-full p-4 rounded-lg border border-[#282828] bg-[#111111] text-[#F5F5F5] hover:border-[#40E0D0]/30 hover:bg-[#40E0D0]/5 transition-all text-center"
            >
              <div className="text-[14px] font-medium">
                {scenario.choice_a_label}
              </div>
            </button>

            <button
              onClick={() => handleChoiceSelect('B')}
              className="w-full p-4 rounded-lg border border-[#282828] bg-[#111111] text-[#F5F5F5] hover:border-[#40E0D0]/30 hover:bg-[#40E0D0]/5 transition-all text-center"
            >
              <div className="text-[14px] font-medium">
                {scenario.choice_b_label}
              </div>
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Outcome */}
      {currentStep === 'outcome' && selectedChoice && (
        <div className="bg-[#1A1A1A] rounded-xl border border-[#282828] overflow-hidden">
          {/* Outcome Content - Scrollable if needed */}
          <div className="p-5 sm:p-6 space-y-6 sm:space-y-8 max-h-[60vh] overflow-y-auto">
            {/* What Happens Next */}
            <div>
              <h3 className="text-[16px] font-semibold mb-3 text-[#40E0D0] tracking-tight">
                What Happens Next
              </h3>
              <div className="text-[#E0E0E0] text-[14px] leading-relaxed whitespace-pre-line">
                {selectedChoice === 'A'
                  ? scenario.choice_a_outcome
                  : scenario.choice_b_outcome}
              </div>
            </div>

            {/* What Most People Miss */}
            <div>
              <h3 className="text-[16px] font-semibold mb-3 text-[#40E0D0] tracking-tight">
                What Most People Miss
              </h3>
              <div className="text-[#E0E0E0] text-[14px] leading-relaxed whitespace-pre-line">
                {scenario.most_people_miss}
              </div>
            </div>
          </div>

          {/* Bottom CTA */}
          <div className="p-5 sm:p-6 pt-0">
            <button
              onClick={handleBackToScenario}
              className="w-full px-4 py-3 rounded-lg border border-[#282828] text-[#F5F5F5] hover:bg-[#282828] transition-colors text-[14px] font-medium"
            >
              Back to scenario
            </button>
          </div>
        </div>
      )}
    </CivicModal>
  );
}
